import { useMemo, type CSSProperties } from "react";
import { ScrollArea, ScrollBar } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { cn } from "@/core/utils/utils";
import { CELL_MARK_COLOR } from "@/payroll/editor/diff-marks";
import type {
  ComparisonCellData,
  ComparisonGrid,
} from "@/payroll/editor/types";
import { COLUMN_LETTERS, coordKey } from "./cell-marks";
import type { CellMark } from "./types";

export { cellAddress, MARK_LABEL, markSet, markCount } from "./cell-marks";
export type { CellMark } from "./types";

// A mark is an inset ring, never a fill: the cell's own background is real
// payroll formatting, and tinting over it hides the thing being compared.
// These are the colours the Admin comparator uses, so one diff reads the same
// way whoever is looking at it.
export const MARK_COLOR = CELL_MARK_COLOR;

// The ring a marked cell wears, built from the one colour above rather than
// restated as classes. That colour is also painted on a canvas, which cannot
// read a class, so a style keeps the two in step.
export const markRing = (mark: CellMark): CSSProperties => ({
  boxShadow: `inset 0 0 0 ${mark === "format" ? 1 : 2}px ${MARK_COLOR[mark]}`,
  outline: mark === "format" ? `1px dashed ${MARK_COLOR[mark]}` : undefined,
  outlineOffset: mark === "format" ? "-3px" : undefined,
});

// Selection replaces the mark rather than stacking with it: one ring per cell,
// and the inspector underneath says what kind of change it is anyway.
//
// It is drawn the way the real workbook draws its active cell, because a reader
// moving between the two should not have to learn the idea twice: the accent
// ring in `--blue-60`, and the row and column headers the cell sits under going
// grey. A 2px inset ring on its own was not enough on a sheet where every cell
// already wears a 2px mark ring — on an all-added sheet the selected cell was
// one blue square in a field of green ones, with nothing above it to say which
// row or column you were in.
//
// Exactly the geometry of a mark ring, so selecting a cell swaps a colour and
// moves nothing. An outer ring was tried and looked wrong for a reason worth
// writing down: it drew the selected cell 1px larger on every side than the
// marked cells beside it, so in a row of ringed boxes one of them no longer
// lined up with the rest. The axis headers are what say where you are; the
// ring only has to say which cell.
//
// It has to be a style, not a class. The mark ring is an inline `boxShadow`,
// and an inline style beats a class however specific the class is, so the
// selection ring lost every time the cell carried a mark — which is every cell
// worth clicking in a diff. The comment above this one claimed selection
// replaced the mark; it did not, it was simply never drawn.
const SELECTED_STYLE: CSSProperties = {
  boxShadow: "inset 0 0 0 2px hsl(var(--blue-60))",
};

/** Raises the cell so a neighbour's ring never paints over its own. */
const SELECTED_RING = "relative z-[6]";

// The axis the active cell sits on, the way a spreadsheet lights it. Grey, not
// the accent: the workbook repaints its selected headers grey for the same
// reason, a solid blue block above the sheet is louder than the selection it
// describes.
const HEADER_ACTIVE = "bg-gray-15 text-primary-text";

const DEFAULT_WIDTH = 132;
/** Which way each arrow key moves the selection, as [row, column]. */
const ARROWS: Record<string, [number, number]> = {
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1],
};

const DEFAULT_HEIGHT = 30;

const cellStyle = (
  cell: ComparisonCellData | undefined,
): React.CSSProperties => {
  const own = cell?.style ?? undefined;
  return {
    background: own?.bg,
    color: own?.color,
    fontWeight: own?.bold ? 600 : undefined,
    textAlign: own?.align,
    verticalAlign: own?.vertical,
    whiteSpace: own?.wrap ? "normal" : "nowrap",
  };
};

/**
 * One side of the comparison, drawn as the spreadsheet it is: the workbook's own
 * column widths, fills, font colors, alignment and number formatting, because
 * this is the document the reader already knows. Rows and columns keep their
 * real addresses so the two sides line up cell for cell, and marked coordinates
 * are highlighted rather than re-diffed in the browser.
 */
export const ComparisonGridView = ({
  grid,
  marks,
  rows,
  cols,
  emptyLabel,
  viewportRef,
  describe,
  selected,
  onSelect,
}: {
  grid: ComparisonGrid | null;
  marks: Map<string, CellMark>;
  rows: number;
  cols: number;
  emptyLabel: string;
  viewportRef?: React.Ref<HTMLDivElement>;
  describe?: (row: number, col: number) => string | undefined;
  selected?: string | null;
  onSelect?: (coord: string) => void;
}) => {
  const byCoord = useMemo(() => {
    const map = new Map<string, ComparisonCellData>();
    for (const cell of grid?.cells ?? [])
      map.set(coordKey(cell.row, cell.col), cell);
    return map;
  }, [grid]);

  if (!grid)
    return (
      <Stack
        gap="none"
        align="stretch"
        direction="row"
        className="flex h-full items-center justify-center p-6 text-sm text-secondary-text"
      >
        {emptyLabel}
      </Stack>
    );

  // Which row and column the active cell sits on. `selected` is the coord key,
  // so the axis comes from it rather than from a second piece of state that
  // could disagree with the ring.
  const [activeRow, activeCol] = (selected ?? "").split(":").map(Number);
  const widths = grid.column_widths ?? [];
  const heights = grid.row_heights ?? [];

  return (
    <ScrollArea
      className="h-full w-full bg-surface-secondary"
      viewportRef={viewportRef}
    >
      {/* One way in, then the arrow keys.
          A cell was selectable by mouse only: it is a `td` with a click, so
          nothing could reach it from the keyboard. Putting every cell in the
          tab order would mean thousands of stops on a real payroll, so the
          table itself takes the focus and the arrows move the selection, which
          is how a spreadsheet already behaves. */}
      <table
        tabIndex={0}
        aria-label="Comparison cells. Use the arrow keys to move."
        onKeyDown={(event) => {
          const step = ARROWS[event.key];
          if (!step || !onSelect) return;
          event.preventDefault();
          const [row, col] = (selected ?? "").split(":").map(Number);
          const next = coordKey(
            Math.min(Math.max((row || 1) + step[0], 1), rows),
            Math.min(Math.max((col || 1) + step[1], 1), cols),
          );
          onSelect(next);
          event.currentTarget
            .querySelector(`[data-coord="${next}"]`)
            ?.scrollIntoView({ block: "nearest", inline: "nearest" });
        }}
        className="border-separate border-spacing-0 text-md outline-none"
        style={{ tableLayout: "fixed" }}
      >
        <colgroup>
          <col style={{ width: 40 }} />
          {Array.from({ length: cols }, (_, index) => (
            <col
              key={index}
              style={{ width: widths[index] ?? DEFAULT_WIDTH }}
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="sticky left-0 top-0 z-20 h-6 border-b border-r border-stroke-tertiary bg-surface-secondary" />
            {Array.from({ length: cols }, (_, index) => (
              <th
                key={index}
                className={cn(
                  "sticky top-0 z-10 h-6 border-b border-r border-stroke-tertiary bg-surface-secondary px-2 text-center font-semibold text-secondary-text",
                  activeCol === index + 1 && HEADER_ACTIVE,
                )}
              >
                {COLUMN_LETTERS(index + 1)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, rowIndex) => {
            const row = rowIndex + 1;
            return (
              <tr
                key={row}
                style={{ height: heights[rowIndex] ?? DEFAULT_HEIGHT }}
              >
                <th
                  className={cn(
                    "sticky left-0 z-10 border-b border-r border-stroke-tertiary bg-surface-secondary px-1 text-center align-middle font-semibold text-secondary-text",
                    activeRow === row && HEADER_ACTIVE,
                  )}
                >
                  {row}
                </th>
                {Array.from({ length: cols }, (_, colIndex) => {
                  const col = colIndex + 1;
                  const key = coordKey(row, col);
                  const cell = byCoord.get(key);
                  const mark = marks.get(key);
                  const overflow = cell?.overflow ?? undefined;
                  const background = cell?.style?.bg;
                  return (
                    <td
                      key={col}
                      data-coord={key}
                      title={describe?.(row, col) ?? cell?.value}
                      style={{
                        ...cellStyle(cell),
                        // One ring per cell. The inspector underneath says what
                        // kind of change it is, so the mark can stand down while
                        // the cell is the one being read.
                        ...(mark && selected !== key ? markRing(mark) : {}),
                        ...(selected === key ? SELECTED_STYLE : {}),
                      }}
                      onClick={() => onSelect?.(key)}
                      className={cn(
                        "relative cursor-pointer border-b border-r border-stroke-tertiary bg-surface-primary px-2 py-[5px] align-middle",
                        // A long label runs across the empty cells beside it,
                        // the way it does in the spreadsheet itself.
                        {
                          "overflow-visible": overflow,
                          "overflow-hidden text-ellipsis": !overflow,
                        },
                        selected === key && SELECTED_RING,
                      )}
                    >
                      {overflow ? (
                        <span
                          className={cn(
                            "pointer-events-none absolute inset-y-0 left-0 z-[2] flex items-center px-2",
                            { "bg-surface-primary": !background },
                          )}
                          style={{ width: overflow, background }}
                        >
                          {cell?.value}
                        </span>
                      ) : (
                        (cell?.value ?? "")
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
};
